import { STAFF_PATHS } from '../utils/staffPaths';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ThemeToggle, PasswordInput } from '../v2/auth/AuthShell';
import StaffDesign from './StaffDesign';
import { getStaffLandingPath, isAdminAuthenticated, removeAdminToken } from '../services/authService';
import { staffJSON, completeStaffSignIn } from './staffApi';


const Login = () => {
    const location = useLocation();
    const [error, setError] = useState(location.state?.staffSignInError || '');
    const [submitting, setSubmitting] = useState(false);
    const [step2, setStep2] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [timer, setTimer] = useState(900); // 15 minutes
    const [isCodeExpired, setIsCodeExpired] = useState(false);
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const [mfaMethod, setMfaMethod] = useState('email');
    const [challenge, setChallenge] = useState('');

    // Countdown timer
    useEffect(() => {
        let countdown;
        if (isCodeSent && timer > 0) {
            countdown = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(countdown);
                        setIsCodeExpired(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdown);
    }, [isCodeSent, timer]);

    // Redirect if already logged in
    useEffect(() => {
        if (isAdminAuthenticated()) navigate(getStaffLandingPath());
    }, [navigate]);

    // Public sign-in and authenticated profile checks use the same /s1 proxy.
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;
        setSubmitting(true); setError(''); setMessage(''); removeAdminToken();
        try {
            const data = await staffJSON('/login', { method: 'POST', authenticated: false, body: JSON.stringify({ email, password }) });
            setChallenge(data.challenge); setMfaMethod(data.mfaMethod === 'totp' ? 'totp' : 'email');
            setStep2(true); setMessage(data.message); setIsCodeExpired(false); setTimer(900); setIsCodeSent(true);
        } catch (err) { setError(err.message || 'Unable to start staff sign-in.'); }
        finally { setSubmitting(false); }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        if (submitting) return;
        setSubmitting(true); setError(''); setMessage('');
        try {
            const data = await staffJSON('/verify-login', { method: 'POST', authenticated: false, body: JSON.stringify({ email, password, code, challenge }) });
            const profile = await completeStaffSignIn(data.admintoken);
            navigate(profile.role === 'marketing' ? STAFF_PATHS.marketing : STAFF_PATHS.dashboard, { replace: true });
        } catch (err) {
            removeAdminToken();
            setError(err.message || 'Verification failed.');
        } finally { setSubmitting(false); }
    };

    const handleResendCode = async () => {
        if (submitting) return;
        setSubmitting(true); setError(''); setMessage('');
        try {
            const data = await staffJSON('/resend-login', { method: 'POST', authenticated: false, body: JSON.stringify({ email, password }) });
            setChallenge(data.challenge); setMfaMethod(data.mfaMethod === 'totp' ? 'totp' : 'email');
            setIsCodeExpired(false); setTimer(900); setIsCodeSent(true); setMessage(data.message);
        } catch (err) { setError(err.message || 'Failed to resend code.'); }
        finally { setSubmitting(false); }
    };

    const restartSignIn = () => {
        setChallenge(''); setMfaMethod('email');
        removeAdminToken(); setStep2(false); setCode(''); setPassword(''); setError(''); setMessage('');
        setIsCodeSent(false); setIsCodeExpired(false); setTimer(900);
    };

    const busy = submitting;
    // Same layout and classes as the customer sign-in (v2/auth/AuthShell), so the two
    // pages read as one product. StaffDesign stays the root for the staff MUI theme.
    return <StaffDesign className="pq-auth staff-login">
        <div className="pq-auth-card">
            <nav className="pnav" aria-label="Staff sign-in navigation">
                <Link to={STAFF_PATHS.login} className="pnav-brand">
                    <img className="pnav-mark" src="/images/pipsqueak-mark.png" alt="" />
                    <span>Pipsqueak</span>
                    <span className="staff-internal-tag">Internal</span>
                </Link>
                <ThemeToggle />
            </nav>
            <div className="auth">
                <div className="auth-form"><div className="auth-form-inner">
                    <span className="auth-eyebrow">Pipsqueak staff workspace</span>
                    <h1 className="auth-title">{step2 ? (mfaMethod === 'totp' ? 'Authenticator code' : 'Verification Code') : 'Staff sign in'}</h1>
                    <p className="auth-sub">{step2 ? (mfaMethod === 'totp' ? 'Enter the current code from your Pipsqueak Staff authenticator entry, or a recovery code.' : `Enter the verification code sent to ${email}.`) : 'Your workspace for administration and Marketing.'}</p>
                    {!step2 ? <>
                        <form onSubmit={handleSubmit} className="auth-fields">
                            <div>
                                <label className="auth-label" htmlFor="staff-email">Email <span className="auth-req">*</span></label>
                                <input id="staff-email" className="auth-input" type="email" autoComplete="username" placeholder="you@btsg.io" required value={email} disabled={busy} onChange={e => { setEmail(e.target.value); setError(''); setMessage(''); }} />
                            </div>
                            <div>
                                <label className="auth-label" htmlFor="staff-password">Password <span className="auth-req">*</span></label>
                                <PasswordInput id="staff-password" autoComplete="current-password" placeholder="Your password" required value={password} disabled={busy} onChange={e => { setPassword(e.target.value); setError(''); setMessage(''); }} />
                            </div>
                            {error && <div role="alert" className="auth-note">{error}</div>}
                            {message && <div role="status" className="auth-note auth-note--ok">{message}</div>}
                            <button type="submit" className="auth-btn" disabled={busy}>{submitting ? 'Sending code...' : 'Login'}</button>
                        </form>
                        <p className="auth-help staff-login-note">Sign in with your staff password, then verify with your authenticator app or email. First-time users can set up an authenticator after email verification.</p>
                    </> : <>
                        <form onSubmit={handleVerify} className="auth-fields">
                            <div>
                                <label className="auth-label" htmlFor="staff-code">{mfaMethod === 'totp' ? 'Authenticator code' : 'Verification Code'}</label>
                                <input id="staff-code" className="auth-input" type="text" inputMode={mfaMethod === 'totp' ? 'text' : 'numeric'} autoComplete="one-time-code" placeholder={mfaMethod === 'totp' ? '123456 or a recovery code' : '6-digit code'} required value={code} disabled={busy || isCodeExpired} onChange={e => setCode(e.target.value)} />
                                <p className="auth-help">{isCodeExpired ? (mfaMethod === 'totp' ? 'Sign-in expired. Go back and start again.' : 'Code expired. Please resend.') : `Sign-in expires in: ${Math.floor(timer / 60)}:${('0' + (timer % 60)).slice(-2)}`}</p>
                            </div>
                            {error && <div role="alert" className="auth-note">{error}</div>}
                            {message && <div role="status" className="auth-note auth-note--ok">{message}</div>}
                            <button type="submit" className="auth-btn" disabled={busy || isCodeExpired}>{submitting ? 'Checking session...' : 'Verify'}</button>
                            {mfaMethod === 'email' && <button type="button" className="auth-btn auth-btn--ghost" disabled={!isCodeExpired || busy} onClick={handleResendCode}>Resend verification code</button>}
                            <button type="button" className="auth-btn auth-btn--ghost" disabled={busy} onClick={restartSignIn}>Back to sign in</button>
                        </form>
                    </>}
                </div></div>
                <aside className="auth-brand">
                    <img className="auth-shield" src="/images/pipsqueak-shield.png" alt="" />
                    <p className="auth-quote">One workspace for the platform, its tenants, and what comes next.</p>
                    <div className="auth-stats">
                        <div className="auth-stat"><b>Tenants</b><span>Manage the platform</span></div>
                        <div className="auth-stat"><b>Growth</b><span>Trials and renewals</span></div>
                        <div className="auth-stat"><b>Roles</b><span>Access by staff role</span></div>
                    </div>
                </aside>
            </div>
        </div>
    </StaffDesign>;
};

export default Login;
